import React, { useState, useEffect } from "react";
import { useAuth0 } from "@auth0/auth0-react";

import Popup from "reactjs-popup";
import "reactjs-popup/dist/index.css";
import { styled } from "@mui/material/styles";
import Paper from "@mui/material/Paper";
import { Skeleton } from "@mui/material";
import {
  Box,
  Typography,
  Container,
  Unstable_Grid2 as Grid2,
  ClickAwayListener,
  Grid,
  LinearProgress,
} from "@mui/material";
import KeyboardArrowUpRoundedIcon from "@mui/icons-material/KeyboardArrowUpRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import CircularProgress from "@mui/material/CircularProgress";
import {
  BodyLarge,
  BodyMedium,
  BodySmall,
  H5HeadlineMedium,
  H7TitleLarge,
  H8TitleMedium,
  H9TitleSmall,
  LabelLarge,
  LabelMedium,
  LabelSmall,
} from "../components/StyledComponents/Typography/Typography.tsx";
import { useTheme } from "../ThemeContext/ThemeContext.js";
import {
  FetchDashboardStatistics,
  FetchDashboardStatisticsLED,
  GetCurrentTHsPowerForDashboard,
  GetIssuesCountForDashboard,
  GetSiteTemperatures,
  GetUserUseQuery,
} from "../api/api";
import { useHistory } from "react-router-dom";

import LineChart from "./LineChart/LineChart";
import {
  apiBodyConstructorForDashboard,
  apiBodyConstructorForCurrentStats,
  apiBodyConstructorForSiteLocationStats,
} from "../api/apiHelpers";
import useAppContextProvider from "../AppContext/useAppContextProvider";
import { useQuery } from "@tanstack/react-query";
import { generateConfigForGraphs, generateDataForGraphs } from "./utils/utils";
import AuradineDataNotFoundState from "../components/StyledComponents/EmptyStates/AuradineDataNotFoundState.tsx";
import AuradineErrorState from "../components/StyledComponents/EmptyStates/AuradineErrorState.tsx";
import { useGetSiteLists } from "../api/queries/useGetSiteLists.ts";
import { GroupOperationsTab } from "./GroupOperations/GroupOperations.js";
import { capitalizeString } from "../components/util.js";
import Map from "./Map/Map.js";
import {
  StyledMenu,
  StyledMenuItem,
} from "../components/StyledComponents/Menu/Menu.tsx";
import {
  OutlinedButton,
  StyledIconButton,
  StyledLabel,
} from "../components/StyledComponents/Buttons/AuradineButtons.js";
import { isEmpty } from "lodash";
import PageWrapper from "../components/PageWrapper/PageWrapper.js";

export const MetricCard = styled(Paper)(({ theme, colors }) => ({
  backgroundColor: colors?.background?.card || (theme.palette.mode === "dark" ? "#1A2027" : "#fff"),
  border: `1px solid ${colors?.border?.card}`,
  paddingBottom: theme.spacing(4),
  paddingTop: theme.spacing(4),
  borderRadius: "1rem",
  width: "100%",
  height: "100%",
}));



export const ChartCard = styled(Paper)(({ theme, colors }) => ({
  backgroundColor: colors?.background?.card || (theme.palette.mode === "dark" ? "#1A2027" : "#fff"),
  border: `1px solid ${colors?.border?.card}`,
  paddingBottom: theme.spacing(4),
  paddingTop: theme.spacing(4),
  borderRadius: "1rem",
  textAlign: "center",
  width: "100%",
  height: "100%",
}));

export const MinimizedChartCard = styled(Paper)(({ theme, colors }) => ({
  backgroundColor: colors?.background?.card || (theme.palette.mode === "dark" ? "#1A2027" : "#fff"),
  border: `1px solid ${colors?.border?.card}`, 
  paddingBottom: theme.spacing(2),
  paddingTop: theme.spacing(2),
  borderRadius: "1rem",
  textAlign: "center",
  width: "100%",
  height: "100%",
}));

export const DashboardTitleTypography = styled(Typography)(({ theme, colors }) => ({
  ...theme.typography.h6,
  fontSize: "1.5rem",
  fontWeight: 400,
  color: colors?.text?.primary,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}));

export const LineChartTitleTypography = styled(Typography)(({ theme, colors }) => ({
  ...theme.typography.body2,
  fontSize: "1.125rem",
  fontWeight: 400,
  color: colors?.text?.secondary,
}));

export const LoadingSpinner = styled(CircularProgress)(({ theme }) => ({
  color: theme.palette.primary.dark,
}));

// LinearProgressWithLabel component
const LinearProgressWithLabel = ({
  value,
  label,
  count,
  total,
  color,
  colors,
}) => {
  const progressColor = color || colors?.primary?.main
  
  return (
    <Box display="flex" flexDirection="column" gap={1}>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <LabelMedium style={{ color: colors?.text?.secondary }}>{label}</LabelMedium>
        <Box display="flex" gap={1} alignItems="center">
          <BodySmall style={{ color: colors?.text?.muted }}>
            {count}/{total}
          </BodySmall>
          <BodySmall style={{ color: colors?.text?.muted }}>
            {value.toFixed(1)}%
          </BodySmall>
        </Box>
      </Box>
      <LinearProgress
        variant="determinate"
        value={value}
        sx={{
          height: 8,
          borderRadius: 4,
          backgroundColor: colors?.border?.muted,
          "& .MuiLinearProgress-bar": {
            backgroundColor: progressColor,
            borderRadius: 4,
          },
        }}
      />
    </Box>
  );
};

const Home = () => {
  /*ToDo: We encountered an issue where the navbar designed by Diana has a maximum height of 48px, while the current navbar has a height of 60px. This discrepancy caused the content to overflow and require scrolling when placed as specified in Figma. To address this, we made some adjustments to the dimensions of a few components.
    The filters were given a maximum height of 2rem, and the container housing them was adjusted accordingly.
    The expanded graph, originally intended to have a height of 368px, was reduced to 328px to accommodate all graphs on one page without causing overflow or cutting off content.
    The smaller graphs were kept at the exact heights specified in the design. */
  const {
    selectedValue,
    setSelectedValue,
    utcTimeStartString,
    utcTimeEndString,
    binSize,
    calculateDateTimeValues,
    timeFilterValues: timeRanges,
    selectedGroup,
    setSelectedGroup,
  } = useAppContextProvider();

  const { colors } = useTheme();

  const history = useHistory();

  // State declarations
  const [authToken, setAuthToken] = useState("");
  const [selectedSite, setSelectedSite] = useState("");
  const [selectedGroupName, setSelectedGroupName] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const buttonRef = React.useRef(null);
  const open = Boolean(anchorEl);

  // Add expansion state variables
  const [isExpandedGraphsModeActivated, setIsExpandedGraphsModeActivated] = useState(false);
  const [expandedChartIndex, setExpandedChartIndex] = useState(-1);
  const [expandedChart, setExpandedChart] = useState([]);
  const [otherCharts, setOtherCharts] = useState([]);

  const { isAuthenticated, getAccessTokenSilently, user } = useAuth0();
  useEffect(() => {
    const fetchToken = async () => {
      try {
        if (!isAuthenticated) {
          setAuthToken("");
          console.warn("User is not authenticated. Fetching token...");
          return;
        }
        const token = await getAccessTokenSilently({
          cacheMode: "cache-only",
          ignoreCache: true,
        });
        setAuthToken(token);
      } catch (error) {
        console.error(" Error fetching token:", error);
      }
    };
    fetchToken();
    const interval = setInterval(() => {
      fetchToken();
    }, 5 * 60 * 1000);

    // Clean up the interval on unmount
    return () => clearInterval(interval);
  }, [isAuthenticated, getAccessTokenSilently]);

  //useEffect to calculate date time values on load of the page
  useEffect(() => {
    document.title = "Home";
    calculateDateTimeValues(selectedValue);
  }, []);

  const handleChange = (event) => {
    calculateDateTimeValues(event.target.value);
  };

  const {
    data: siteLists,
    isLoading: isSiteGroupDataLoading,
    isFetching: isSiteGroupDataFetching,
    isFetched: isSiteGroupDataFetched,
    isRefetching: isSiteGroupDataRefetching,
    refetch,
  } = useGetSiteLists({
    authToken,
    leftPanelOpen: true,
  });

  let siteLocationsList = siteLists?.siteList
    ? siteLists.siteList
        .filter(
          (site) =>
            site?.siteName &&
            site?.siteLocation &&
            site?.location?.lat != null &&
            site?.location?.lng != null &&
            site?.groups &&
            Array.isArray(site?.groups) &&
            Array.isArray(site?.groups) &&
            site.groups.every((group) => "deviceCount" in group)
        )
        .map((site) => ({
          siteName: site.siteName,
          siteLocation: site.siteLocation,
          lat: site.location.lat,
          lng: site.location.lng,
          numberOfMiners:
            site.groups.reduce((sum, group) => sum + group.deviceCount, 0) ??
            null,
        }))
    : [];

  let numSitesWithLocations = siteLists?.siteList
    ? siteLists.siteList.filter(
        (site) => !site?.siteLocation && !site?.location
      ).length
    : null;

  const {
    data: currentTemperatureForSites,
    isFetched: isCurrentTemperatureForSitesFetched,
    isFetching: isCurrentTemperatureForSitesFetching,
    isLoading: isCurrentTemperatureForSitesLoading,
    isRefetching: isCurrentTemperatureForSitesRefetching,
    refetch: refetchCurrentTemperatureForSites,
  } = useQuery({
    queryKey: ["currentTemperatureForSites"],
    queryFn: async ({ queryKey }) => {
      const token = await getAccessTokenSilently();
      const data = await GetSiteTemperatures(token);
      // Add 1 second delay
      await new Promise((resolve) => setTimeout(resolve, 1000));

      return data;
    },
    refetchInterval: 900000,
  });

  if (
    isCurrentTemperatureForSitesFetched &&
    currentTemperatureForSites.status === "ok"
  ) {
    siteLocationsList.forEach((site) => {
      const siteTemperature = currentTemperatureForSites.msg.find(
        (temperature) => temperature.siteName === site.siteName
      );
      if (siteTemperature) {
        site.temperature = siteTemperature.temperature;
      }
    });
  }

  const {
    data: userInfo,
    isFetched: isUserInfoFetched,
    isFetching: isUserInfoFetching,
  } = useQuery({
    queryKey: ["userInfo"],
    queryFn: () => GetUserUseQuery(authToken),
    enabled: !!authToken,
  });

  let userName = "";

  if (isUserInfoFetched) {
    userName =
      userInfo?.user?.name !== "" ? userInfo?.user?.name : user?.nickname;
  }

  // useQuery to fetch device group data from the server
  const siteOptions =
    siteLists && siteLists?.status === "ok" && siteLists?.siteList?.length > 0
      ? siteLists?.siteList.map((site) => site.siteName)
      : [];

  //api options to fetch dashboard data from the server

  const {
    data: dashboardData,
    isFetched: isDashboardDataFetched,
    isFetching: isDashboardDataFetching,
    isLoading: isDashboardDataLoading,
    isRefetching: isDashboardDataRefetching,
    refetch: refetchDashboardData,
  } = useQuery({
    queryKey: [
      "dashboardData",
      selectedSite,
      selectedGroupName,
      selectedValue,
      utcTimeStartString,
      utcTimeEndString,
      binSize,
    ],
    queryFn: async ({ queryKey }) => {
      const apiOptions = apiBodyConstructorForDashboard(
        utcTimeStartString,
        utcTimeEndString,
        binSize,
        authToken,
        selectedGroupName,
        selectedSite
      );

      const data = await FetchDashboardStatistics(queryKey[0], apiOptions);

      // Add 1 second delay
      await new Promise((resolve) => setTimeout(resolve, 1000));

      return data;
    },
    enabled: !!selectedSite,
  });

  const {
    data: dashboardDataLED,
    isFetched: isDashboardDataFetchedLED,
    isFetching: isDashboardDataFetchingLED,
    isLoading: isDashboardDataLoadingLED,
    isRefetching: isDashboardDataRefetchingLED,
    refetch: refetchDashboardDataLED,
  } = useQuery({
    queryKey: [
      "dashboardDataLED",
      selectedSite,
      selectedGroupName,
      selectedValue,
      utcTimeStartString,
      utcTimeEndString,
      binSize,
    ],
    queryFn: async ({ queryKey }) => {
      const apiOptions = apiBodyConstructorForDashboard(
        utcTimeStartString,
        utcTimeEndString,
        binSize,
        authToken,
        selectedGroupName,
        selectedSite
      );

      const data = await FetchDashboardStatisticsLED(queryKey[0], apiOptions);

      // Add 1 second delay
      await new Promise((resolve) => setTimeout(resolve, 1000));

      return data;
    },
    enabled: !!selectedSite,
  });

  const {
    data: currentTHsPowerForDashboard,
    isFetched: isCurrentTHsPowerForDashboardFetched,
    isFetching: isCurrentTHsPowerForDashboardFetching,
    isLoading: isCurrentTHsPowerForDashboardLoading,
    isRefetching: isCurrentTHsPowerForDashboardRefetching,
    refetch: refetchCurrentTHsPower,
  } = useQuery({
    queryKey: ["currentTHsPowerForDashboard", selectedGroupName, selectedSite],
    queryFn: async ({ queryKey }) => {
      const token = await getAccessTokenSilently();
      const apiOptions = apiBodyConstructorForCurrentStats(
        token,
        selectedGroupName,
        selectedSite
      );

      const data = await GetCurrentTHsPowerForDashboard(
        queryKey[0],
        apiOptions
      );

      // Add 1 second delay
      await new Promise((resolve) => setTimeout(resolve, 1000));

      return data;
    },
    refetchInterval: 60000,
  });

  const {
    data: currentIssuesCountForDashboard,
    isFetched: isCurrentIssuesCountForDashboardFetched,
    isFetching: isCurrentIssuesCountForDashboardFetching,
    isLoading: isCurrentIssuesCountForDashboardLoading,
    isRefetching: isCurrentIssuesCountForDashboardRefetching,
    refetch: refetchIssuesCount,
  } = useQuery({
    queryKey: [
      "currentIssuesCountForDashboard",
      selectedGroupName,
      selectedSite,
    ],
    queryFn: async ({ queryKey }) => {
      const token = await getAccessTokenSilently();
      const apiOptions = apiBodyConstructorForCurrentStats(
        token,
        selectedGroupName,
        selectedSite
      );

      const data = await GetIssuesCountForDashboard(queryKey[0], apiOptions);

      // Add 1 second delay
      await new Promise((resolve) => setTimeout(resolve, 1000));

      return data;
    },
    refetchInterval: 60000,
  });

  const totalIssuesCount =
    (currentIssuesCountForDashboard?.msg?.hashboardIssuesCount ?? 0) +
    (currentIssuesCountForDashboard?.msg?.fanIssuesCount ?? 0) +
    (currentIssuesCountForDashboard?.msg?.psuIssuesCount ?? 0) +
    (currentIssuesCountForDashboard?.msg?.leakDetectionCount ?? 0) +
    (currentIssuesCountForDashboard?.msg?.invalidPoolConfigCount ?? 0);

  //generate data for the graphs

  const dataForGraphs = generateDataForGraphs(
    utcTimeStartString,
    utcTimeEndString,
    binSize,
    dashboardData,
    dashboardDataLED
  );

  // generate configurations for the graphs
  const {
    hashRateGraphConfig,
    powerGraphConfig,
    tempuratureGraphConfig,
    controlBoardTempGraphConfig,
    uptimeGraphConfig,
    issuesGraphConfig,
    hasIssuesData,
  } = generateConfigForGraphs(dataForGraphs, binSize);

  // Issues graph is always displayed separately, not part of expansion logic
  const issuesGraphData = {
    heading: "Issues Over Time",
    subHeading: "in count",
    chartOptions: issuesGraphConfig,
    hasIssuesData: hasIssuesData,
    isExpanded: false,
    isExpansionRequired: false,
  };

  // Only the 4 performance graphs can be expanded
  let lineChartsData = [
    {
      heading: "Average Hashrate",
      subHeading: "in TH/s",
      chartOptions: hashRateGraphConfig,
      isExpanded: false,
      isExpansionRequired: true,
    },
    {
      heading: "Power",
      subHeading: "in kW",
      chartOptions: powerGraphConfig,
      isExpanded: false,
      isExpansionRequired: true,
    },
    {
      heading: "Average Chip Temperature",
      subHeading: "in ˚C",
      chartOptions: tempuratureGraphConfig,
      isExpanded: false,
      isExpansionRequired: true,
    },
    {
      heading: "Average Control Board Temperature",
      subHeading: "in ˚C",
      chartOptions: controlBoardTempGraphConfig,
      isExpanded: false,
      isExpansionRequired: true,
    },
  ];

  // useEffect for chart expansion logic
  useEffect(() => {
    if (expandedChartIndex !== -1) {
      const chartData = lineChartsData[expandedChartIndex];
      setExpandedChart([chartData]);
      setOtherCharts(
        lineChartsData.filter((_, index) => index !== expandedChartIndex)
      );
      setIsExpandedGraphsModeActivated(true);
    }
    if (expandedChartIndex === -1) {
      setExpandedChart([]);
      setOtherCharts([]);
      setIsExpandedGraphsModeActivated(false);
    }
  }, [expandedChartIndex, dashboardData]);

  const handleClick = (event) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMenuClose = (view) => {
    calculateDateTimeValues(view);
    setAnchorEl(null);
  };

  const handleClickAway = () => {
    setAnchorEl(null);
  };

  const setMenuOpen = () => {
    setAnchorEl(buttonRef.current);
  };

  return (
  <PageWrapper
      sx={{ gap: 6 }}
    >
      {isUserInfoFetching ? (
        <Skeleton
          variant="rectangular"
          width={"auto"}
          animation={"wave"}
          height={200}
          sx={{
            borderRadius: 2,
            backgroundColor: colors.background.secondary,
          }}
        />
      ) : (
        <Box
          className={"header-container"}
          display={"flex"}
          justifyContent="flex-start"
          flexDirection="column"
          gap={4}
        >
          <Box
            className={"title-box"}
            display={"flex"}
            flexDirection="column"
            justifyContent={"space-between"}
            gap={3.5}
          >
            <H7TitleLarge>Welcome to FluxVision, {userName}!</H7TitleLarge>
            <BodyLarge>
              Monitor your mining performance through Real-Time Data for your
              organization, sites, and groups. Select a Site or Group to view
              Analytics with time-range controls.
            </BodyLarge>
          </Box>
        </Box>
      )}
      <Box display={"flex"} flexDirection={"column"} gap={6}>
        <Grid
          container
          className={"home-page-container"}
          display={"flex"}
          flexDirection={"row"}
          sx={{ transition: "all 0.3s ease-in-out" }}
        >
          <Box
            width="100%"
            py={4}
            display="flex"
            flexDirection="column"
            gap={6}
            sx={{
              transition: "width 0.3s ease-in-out",
              borderRadius: "1rem 1rem 1rem 1rem",
            }}
          >
            <Box
              className={"filter-box"}
              display={"flex"}
              flexDirection={"row"}
              justifyContent={"space-between"}
              alignItems={"center"}
              flex={1}
              maxHeight={"2rem"}
            >
              <Box
                sx={{
                  backgroundColor: colors.background.accent,
                  padding: "0.5rem 1rem",
                  borderRadius: "1rem",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    backgroundColor: colors.success.main,
                    animation: "pulse 2s infinite",
                    "@keyframes pulse": {
                      "0%": { opacity: 1 },
                      "50%": { opacity: 0.5 },
                      "100%": { opacity: 1 },
                    },
                    borderRadius: "50%",
                  }}
                  marginRight={"0.5rem"}
                />
                <H8TitleMedium style={{ fontSize: "1rem", fontWeight: 400 }}>
                  Real-Time Data
                </H8TitleMedium>
              </Box>
              <Box
                display={"flex"}
                flexDirection={"row"}
                alignItems={"center"}
                gap={2}
              >
                <H8TitleMedium> View data for: </H8TitleMedium>
                <GroupOperationsTab
                  selectedSite={selectedSite}
                  setSelectedSite={setSelectedSite}
                  selectedGroup={selectedGroupName}
                  setSelectedGroup={setSelectedGroupName}
                  user={userInfo?.user}
                  siteLists={siteLists}
                  isDashboardDataFetching={isDashboardDataFetching}
                  isDashboardDataLoading={isDashboardDataLoading}
                  isDashboardDataRefetching={isDashboardDataRefetching}
                />
              </Box>
            </Box>
            <Map
              siteLocationsList={siteLocationsList}
              numSitesWithLocations={numSitesWithLocations}
              selectedSite={selectedSite}
              selectedGroupName={selectedGroupName}
              setSelectedSite={setSelectedSite}
              setSelectedGroupName={setSelectedGroupName}
            />
            <Box display="flex" flex-direction={"row"} gap={4}>
              <MetricCard elevation={0} colors={colors}>
                <>
                  <Box
                    display={"flex"}
                    flexDirection={"row"}
                    justifyContent={"space-between"}
                    padding={"0.675rem 1.5rem"}
                  >
                    <Box display="flex" alignItems="center" gap={2}>
                      <LabelLarge>Aggregated Power Draw</LabelLarge>
                      <span
                        className="material-symbols-outlined"
                        style={{ fontSize: "1rem", color: colors.text.primary }}
                      >
                        power
                      </span>
                    </Box>
                  </Box>
                  <Box
                    display={"flex"}
                    flexDirection={"column"}
                    justifyContent={"flex-start"}
                    padding={"0.675rem 1.5rem"}
                    gap={2}
                  >
                    <Box
                      display={"flex"}
                      flexDirection={"row"}
                      alignItems={"flex-end"}
                      gap={2}
                    >
                      <H5HeadlineMedium style={{ lineHeight: "1.75rem" }}>
                        {isCurrentTHsPowerForDashboardLoading ? (
                          <Skeleton
                            variant="rectangular"
                            width={80}
                            height={32}
                            sx={{ borderRadius: 2, backgroundColor: colors.background.secondary }}
                          />
                        ) : (
                          currentTHsPowerForDashboard?.msg?.aggregatedPowerConsumption || 0
                        )}
                      </H5HeadlineMedium>
                      <BodyLarge style={{ color: colors.text.muted }}>KW</BodyLarge>
                    </Box>
                  </Box>
                </>
              </MetricCard>
              <MetricCard elevation={0} colors={colors}>
                <>
                  <Box
                    display={"flex"}
                    flexDirection={"row"}
                    justifyContent={"space-between"}
                    padding={"0.675rem 1.5rem"}
                  >
                    <Box display="flex" alignItems="center" gap={2}>
                      <LabelLarge>Aggregated Hash Rate</LabelLarge>
                      <span
                        className="material-symbols-outlined"
                        style={{ fontSize: "1rem", color: colors.text.primary }}
                      >
                        vital_signs
                      </span>
                    </Box>
                  </Box>
                  <Box
                    display={"flex"}
                    flexDirection={"column"}
                    justifyContent={"flex-start"}
                    padding={"0.675rem 1.5rem"}
                    gap={2}
                  >
                    <Box
                      display={"flex"}
                      flexDirection={"row"}
                      alignItems={"flex-end"}
                      gap={2}
                    >
                      <H5HeadlineMedium style={{ lineHeight: "1.75rem" }}>
                        {isCurrentTHsPowerForDashboardLoading ? (
                          <Skeleton
                            variant="rectangular"
                            width={80}
                            height={32}
                            sx={{ borderRadius: 2, backgroundColor: colors.background.secondary }}
                          />
                        ) : Number.isFinite(
                            Number(
                              currentTHsPowerForDashboard?.msg?.aggregatedTHs
                            )
                          ) ? (
                          Number(
                            currentTHsPowerForDashboard.msg.aggregatedTHs
                          ) !== 0 ? (
                            (
                              Number(
                                currentTHsPowerForDashboard.msg.aggregatedTHs
                              ) / 1000
                            ).toFixed(3)
                          ) : (
                            0
                          )
                        ) : (
                          0
                        )}
                      </H5HeadlineMedium>
                      <BodyLarge style={{ color: colors.text.muted }}>PH/s</BodyLarge>
                    </Box>
                  </Box>
                </>
              </MetricCard>
              <MetricCard elevation={0} colors={colors}>
                <>
                  <Box
                    display={"flex"}
                    flexDirection={"row"}
                    justifyContent={"space-between"}
                    padding={"0.675rem 1.5rem"}
                  >
                    <Box display="flex" alignItems="center" gap={2}>
                      <LabelLarge>Min Hash Rate</LabelLarge>
                      <span
                        className="material-symbols-outlined"
                        style={{ fontSize: "1rem", color: colors.text.primary }}
                      >
                        arrow_downward
                      </span>
                    </Box>
                    {currentTHsPowerForDashboard?.msg?.minTHs !== undefined &&
                      currentTHsPowerForDashboard?.msg?.count !== 0 && (
                        <StyledIconButton
                          colors={colors}
                          onClick={(event) => {
                            event.stopPropagation();
                            const raw =
                              Math.floor(currentTHsPowerForDashboard?.msg?.minTHs) ?? 0;
                            const value = Number(raw);
                            if (!Number.isFinite(value)) return;
                            const min = Math.max(0, value);
                            const params = new URLSearchParams({
                              minimumHashRate: String(min),
                            });
                            if (selectedSite) {
                              params.append("siteName", selectedSite);
                            }
                            if (selectedGroupName) {
                              params.append("dgname", selectedGroupName);
                            }
                            history.push(`/minerstatus?${params.toString()}`);
                            try {
                              const storageToClear = [
                                "minerFilters",
                                "siteGroup",
                              ];
                              storageToClear.forEach((key) =>
                                sessionStorage.removeItem(key)
                              );
                              const newMinerFilters = { minimumHashRate: min };
                              const newSiteGroupFilters =
                                selectedSite !== "" && selectedGroupName !== ""
                                  ? {
                                      siteName: selectedSite,
                                      dgname: selectedGroupName,
                                    }
                                  : selectedSite !== ""
                                  ? { siteName: selectedSite }
                                  : {};
                              sessionStorage.setItem(
                                "minerFilters",
                                JSON.stringify(newMinerFilters)
                              );
                              sessionStorage.setItem(
                                "siteGroup",
                                JSON.stringify(newSiteGroupFilters)
                              );
                            } catch (e) {
                              console.error(
                                "Failed to update sessionStorage:",
                                e
                              );
                            }
                          }}
                          sx={{ p: 0 }}
                        >
                          <span
                            className="material-symbols-outlined"
                            style={{ fontSize: "1.1rem" }}
                          >
                            open_in_new
                          </span>
                        </StyledIconButton>
                      )}
                  </Box>
                  <Box
                    display={"flex"}
                    flexDirection={"column"}
                    justifyContent={"flex-start"}
                    padding={"0.675rem 1.5rem"}
                    gap={2}
                  >
                    <Box
                      display={"flex"}
                      flexDirection={"row"}
                      alignItems={"flex-end"}
                      gap={2}
                    >
                      <H5HeadlineMedium style={{ lineHeight: "1.75rem" }}>
                        {isCurrentTHsPowerForDashboardLoading ? (
                          <Skeleton
                            variant="rectangular"
                            width={80}
                            height={32}
                            sx={{ borderRadius: 2, backgroundColor: colors.background.secondary }}
                          />
                        ) : (
                          currentTHsPowerForDashboard?.msg?.minTHs || 0
                        )}
                      </H5HeadlineMedium>
                      <BodyLarge style={{ color: colors.text.muted }}>TH/s</BodyLarge>
                    </Box>
                  </Box>
                </>
              </MetricCard>
              <MetricCard elevation={0} colors={colors}>
                <>
                  <Box
                    display={"flex"}
                    flexDirection={"row"}
                    justifyContent={"space-between"}
                    padding={"0.675rem 1.5rem"}
                  >
                    <Box display="flex" alignItems="center" gap={2}>
                      <LabelLarge>Max Hash Rate</LabelLarge>
                      <span
                        className="material-symbols-outlined"
                        style={{ fontSize: "1rem", color: colors.text.primary }}
                      >
                        arrow_upward
                      </span>
                    </Box>
                    {currentTHsPowerForDashboard?.msg?.maxTHs !== undefined &&
                      currentTHsPowerForDashboard?.msg?.count !== 0 && (
                        <StyledIconButton
                          colors={colors}
                          onClick={(event) => {
                            event.stopPropagation();
                            const raw =
                              Math.ceil(currentTHsPowerForDashboard?.msg?.maxTHs) ?? 0;
                            const value = Number(raw);
                            if (!Number.isFinite(value)) return;
                            const max = Math.max(0, value);
                            const params = new URLSearchParams({
                              maximumHashRate: String(max),
                            });
                            if (selectedSite) {
                              params.append("siteName", selectedSite);
                            }
                            if (selectedGroupName) {
                              params.append("dgname", selectedGroupName);
                            }
                            history.push(`/minerstatus?${params.toString()}`);
                            try {
                              const storageToClear = [
                                "minerFilters",
                                "siteGroup",
                              ];
                              storageToClear.forEach((key) =>
                                sessionStorage.removeItem(key)
                              );
                              const newMinerFilters = { maximumHashRate: max };
                              const newSiteGroupFilters =
                                selectedSite !== "" && selectedGroupName !== ""
                                  ? {
                                      siteName: selectedSite,
                                      dgname: selectedGroupName,
                                    }
                                  : selectedSite !== ""
                                  ? { siteName: selectedSite }
                                  : {};
                              sessionStorage.setItem(
                                "minerFilters",
                                JSON.stringify(newMinerFilters)
                              );
                              sessionStorage.setItem(
                                "siteGroup",
                                JSON.stringify(newSiteGroupFilters)
                              );
                            } catch (e) {
                              console.error(
                                "Failed to update sessionStorage:",
                                e
                              );
                            }
                          }}
                          sx={{ p: 0 }}
                        >
                          <span
                            className="material-symbols-outlined"
                            style={{ fontSize: "1.1rem" }}
                          >
                            open_in_new
                          </span>
                        </StyledIconButton>
                      )}
                  </Box>
                  <Box
                    display={"flex"}
                    flexDirection={"column"}
                    justifyContent={"flex-start"}
                    padding={"0.675rem 1.5rem"}
                    gap={2}
                  >
                    <Box
                      display={"flex"}
                      flexDirection={"row"}
                      alignItems={"flex-end"}
                      gap={2}
                    >
                      <H5HeadlineMedium style={{ lineHeight: "1.75rem" }}>
                        {isCurrentTHsPowerForDashboardLoading ? (
                          <Skeleton
                            variant="rectangular"
                            width={80}
                            height={32}
                            sx={{ borderRadius: 2, backgroundColor: colors.background.secondary }}
                          />
                        ) : (
                          currentTHsPowerForDashboard?.msg?.maxTHs || 0
                        )}
                      </H5HeadlineMedium>
                      <BodyLarge style={{ color: colors.text.muted }}>TH/s</BodyLarge>
                    </Box>
                  </Box>
                </>
              </MetricCard>
            </Box>
            <Box display="flex" flex-direction={"row"} gap={4}>
              <MetricCard elevation={0} colors={colors}>
                <Box
                  display={"flex"}
                  flexDirection={"column"}
                  padding={"0.675rem 1.5rem"}
                  sx={{ height: "100%" }}
                  gap={6}
                >
                  <Box display={"flex"} flexDirection={"column"} gap={2}>
                    <LabelLarge>Issue Breakdown</LabelLarge>
                    <BodyMedium style={{ color: colors.text.muted }}>
                      Total Issues: {totalIssuesCount || 0}
                    </BodyMedium>
                  </Box>
                  <Box
                    display="flex"
                    flexDirection="column"
                    justifyContent="space-between"
                    sx={{ flex: 1 }}
                  >
                    <Box sx={{ paddingBottom: "1rem" }}>
                      <LinearProgressWithLabel
                        value={
                          currentIssuesCountForDashboard?.msg
                            ?.hashboardIssuesCount
                            ? (currentIssuesCountForDashboard.msg
                                .hashboardIssuesCount /
                                (currentIssuesCountForDashboard?.msg
                                  ?.totalCount || 1)) *
                              100
                            : 0
                        }
                        colors={colors}
                        label={
                          <Box
                            component="span"
                            sx={{
                              cursor: "pointer",
                              "&:hover": { textDecoration: "underline" },
                            }}
                            onClick={() => {
                              const params = new URLSearchParams({
                                status: "Hash board malfunction",
                              });
                              if (selectedSite)
                                params.append("siteName", selectedSite);
                              if (selectedGroupName)
                                params.append("dgname", selectedGroupName);
                              history.push(`/minerstatus?${params.toString()}`);
                              try {
                                const storageToClear = [
                                  "minerFilters",
                                  "siteGroup",
                                ];
                                storageToClear.forEach((key) =>
                                  sessionStorage.removeItem(key)
                                );
                                const newMinerFilters = {
                                  status: "Hash board malfunction",
                                };
                                const newSiteGroupFilters =
                                  selectedSite !== "" &&
                                  selectedGroupName !== ""
                                    ? {
                                        siteName: selectedSite,
                                        dgname: selectedGroupName,
                                      }
                                    : selectedSite !== ""
                                    ? { siteName: selectedSite }
                                    : {};
                                sessionStorage.setItem(
                                  "minerFilters",
                                  JSON.stringify(newMinerFilters)
                                );
                                sessionStorage.setItem(
                                  "siteGroup",
                                  JSON.stringify(newSiteGroupFilters)
                                );
                              } catch (e) {
                                console.error(
                                  "Failed to update sessionStorage:",
                                  e
                                );
                              }
                            }}
                          >
                            Hashboard Malfunction
                          </Box>
                        }
                        count={
                          currentIssuesCountForDashboard?.msg
                            ?.hashboardIssuesCount || 0
                        }
                        total={
                          currentIssuesCountForDashboard?.msg?.totalCount || 0
                        }
                      />
                    </Box>
                    <Box sx={{ paddingBottom: "1rem" }}>
                      <LinearProgressWithLabel
                        value={
                          currentIssuesCountForDashboard?.msg?.psuIssuesCount
                            ? (currentIssuesCountForDashboard.msg
                                .psuIssuesCount /
                                (currentIssuesCountForDashboard?.msg
                                  ?.totalCount || 1)) *
                              100
                            : 0
                        }
                        colors={colors}
                        label={
                          <Box
                            component="span"
                            sx={{
                              cursor: "pointer",
                              "&:hover": { textDecoration: "underline" },
                            }}
                            onClick={() => {
                              const params = new URLSearchParams({
                                status: "PSU malfunction",
                              });
                              if (selectedSite)
                                params.append("siteName", selectedSite);
                              if (selectedGroupName)
                                params.append("dgname", selectedGroupName);
                              history.push(`/minerstatus?${params.toString()}`);
                              try {
                                const storageToClear = [
                                  "minerFilters",
                                  "siteGroup",
                                ];
                                storageToClear.forEach((key) =>
                                  sessionStorage.removeItem(key)
                                );
                                const newMinerFilters = {
                                  status: "PSU malfunction",
                                };
                                const newSiteGroupFilters =
                                  selectedSite !== "" &&
                                  selectedGroupName !== ""
                                    ? {
                                        siteName: selectedSite,
                                        dgname: selectedGroupName,
                                      }
                                    : selectedSite !== ""
                                    ? { siteName: selectedSite }
                                    : {};
                                sessionStorage.setItem(
                                  "minerFilters",
                                  JSON.stringify(newMinerFilters)
                                );
                                sessionStorage.setItem(
                                  "siteGroup",
                                  JSON.stringify(newSiteGroupFilters)
                                );
                              } catch (e) {
                                console.error(
                                  "Failed to update sessionStorage:",
                                  e
                                );
                              }
                            }}
                          >
                            PSU Malfunction
                          </Box>
                        }
                        count={
                          currentIssuesCountForDashboard?.msg?.psuIssuesCount ||
                          0
                        }
                        total={
                          currentIssuesCountForDashboard?.msg?.totalCount || 0
                        }
                      />
                    </Box>
                    <Box sx={{ paddingBottom: "1rem" }}>
                      <LinearProgressWithLabel
                        value={
                          currentIssuesCountForDashboard?.msg?.fanIssuesCount
                            ? (currentIssuesCountForDashboard.msg
                                .fanIssuesCount /
                                (currentIssuesCountForDashboard?.msg
                                  ?.totalCount || 1)) *
                              100
                            : 0
                        }
                        colors={colors}
                        label={
                          <Box
                            component="span"
                            sx={{
                              cursor: "pointer",
                              "&:hover": { textDecoration: "underline" },
                            }}
                            onClick={() => {
                              const params = new URLSearchParams({
                                status: "Fan malfunction",
                              });
                              if (selectedSite)
                                params.append("siteName", selectedSite);
                              if (selectedGroupName)
                                params.append("dgname", selectedGroupName);
                              history.push(`/minerstatus?${params.toString()}`);
                              try {
                                const storageToClear = [
                                  "minerFilters",
                                  "siteGroup",
                                ];
                                storageToClear.forEach((key) =>
                                  sessionStorage.removeItem(key)
                                );
                                const newMinerFilters = {
                                  status: "Fan malfunction",
                                };
                                const newSiteGroupFilters =
                                  selectedSite !== "" &&
                                  selectedGroupName !== ""
                                    ? {
                                        siteName: selectedSite,
                                        dgname: selectedGroupName,
                                      }
                                    : selectedSite !== ""
                                    ? { siteName: selectedSite }
                                    : {};
                                sessionStorage.setItem(
                                  "minerFilters",
                                  JSON.stringify(newMinerFilters)
                                );
                                sessionStorage.setItem(
                                  "siteGroup",
                                  JSON.stringify(newSiteGroupFilters)
                                );
                              } catch (e) {
                                console.error(
                                  "Failed to update sessionStorage:",
                                  e
                                );
                              }
                            }}
                          >
                            Fan Malfunction
                          </Box>
                        }
                        count={
                          currentIssuesCountForDashboard?.msg?.fanIssuesCount ||
                          0
                        }
                        total={
                          currentIssuesCountForDashboard?.msg?.totalCount || 0
                        }
                      />
                    </Box>
                    <Box sx={{ paddingBottom: "1rem" }}>
                      <LinearProgressWithLabel
                        value={
                          currentIssuesCountForDashboard?.msg
                            ?.invalidPoolConfigCount
                            ? (currentIssuesCountForDashboard.msg
                                .invalidPoolConfigCount /
                                (currentIssuesCountForDashboard?.msg
                                  ?.totalCount || 1)) *
                              100
                            : 0
                        }
                        colors={colors}
                        label={
                          <Box
                            component="span"
                            sx={{
                              cursor: "pointer",
                              "&:hover": { textDecoration: "underline" },
                            }}
                            onClick={() => {
                              const params = new URLSearchParams({
                                status: "Pool configuration invalid",
                              });
                              if (selectedSite)
                                params.append("siteName", selectedSite);
                              if (selectedGroupName)
                                params.append("dgname", selectedGroupName);
                              history.push(`/minerstatus?${params.toString()}`);
                              try {
                                const storageToClear = [
                                  "minerFilters",
                                  "siteGroup",
                                ];
                                storageToClear.forEach((key) =>
                                  sessionStorage.removeItem(key)
                                );
                                const newMinerFilters = {
                                  status: "Pool configuration invalid",
                                };
                                const newSiteGroupFilters =
                                  selectedSite !== "" &&
                                  selectedGroupName !== ""
                                    ? {
                                        siteName: selectedSite,
                                        dgname: selectedGroupName,
                                      }
                                    : selectedSite !== ""
                                    ? { siteName: selectedSite }
                                    : {};
                                sessionStorage.setItem(
                                  "minerFilters",
                                  JSON.stringify(newMinerFilters)
                                );
                                sessionStorage.setItem(
                                  "siteGroup",
                                  JSON.stringify(newSiteGroupFilters)
                                );
                              } catch (e) {
                                console.error(
                                  "Failed to update sessionStorage:",
                                  e
                                );
                              }
                            }}
                          >
                            Invalid Pool Config
                          </Box>
                        }
                        count={
                          currentIssuesCountForDashboard?.msg
                            ?.invalidPoolConfigCount || 0
                        }
                        total={
                          currentIssuesCountForDashboard?.msg?.totalCount || 0
                        }
                      />
                    </Box>
                    <LinearProgressWithLabel
                      value={
                        currentIssuesCountForDashboard?.msg?.leakDetectionCount
                          ? (currentIssuesCountForDashboard.msg
                              .leakDetectionCount /
                              (currentIssuesCountForDashboard?.msg
                                ?.totalCount || 1)) *
                            100
                          : 0
                      }
                      colors={colors}
                      label={
                        <Box
                          component="span"
                          sx={{
                            cursor: "pointer",
                            "&:hover": { textDecoration: "underline" },
                          }}
                          onClick={() => {
                            const params = new URLSearchParams({
                              status: "Leak Detected",
                            });
                            if (selectedSite)
                              params.append("siteName", selectedSite);
                            if (selectedGroupName)
                              params.append("dgname", selectedGroupName);
                            history.push(`/minerstatus?${params.toString()}`);
                            try {
                              const storageToClear = [
                                "minerFilters",
                                "siteGroup",
                              ];
                              storageToClear.forEach((key) =>
                                sessionStorage.removeItem(key)
                              );
                              const newMinerFilters = {
                                status: "Leak Detected",
                              };
                              const newSiteGroupFilters =
                                selectedSite !== "" && selectedGroupName !== ""
                                  ? {
                                      siteName: selectedSite,
                                      dgname: selectedGroupName,
                                    }
                                  : selectedSite !== ""
                                  ? { siteName: selectedSite }
                                  : {};
                              sessionStorage.setItem(
                                "minerFilters",
                                JSON.stringify(newMinerFilters)
                              );
                              sessionStorage.setItem(
                                "siteGroup",
                                JSON.stringify(newSiteGroupFilters)
                              );
                            } catch (e) {
                              console.error(
                                "Failed to update sessionStorage:",
                                e
                              );
                            }
                          }}
                        >
                          Leak Detected
                        </Box>
                      }
                      count={
                        currentIssuesCountForDashboard?.msg
                          ?.leakDetectionCount || 0
                      }
                      total={
                        currentIssuesCountForDashboard?.msg?.totalCount || 0
                      }
                    />
                  </Box>
                </Box>
              </MetricCard>
              <MetricCard elevation={0} colors={colors}>
                <Box
                  display={"flex"}
                  flexDirection={"column"}
                  padding={"0.675rem 1.5rem"}
                  sx={{ height: "100%" }}
                  gap={6}
                >
                  <Box display={"flex"} flexDirection={"column"} gap={2}>
                    <LabelLarge>Miner Status Distribution</LabelLarge>
                    <BodyMedium style={{ color: colors.text.muted }}>
                      Total Miners:{" "}
                      {currentIssuesCountForDashboard?.msg?.totalCount || 0}
                    </BodyMedium>
                  </Box>
                  <Box
                    display="flex"
                    flexDirection="column"
                    justifyContent="space-between"
                    sx={{ flex: 1 }}
                  >
                    <Box sx={{ paddingBottom: "1rem" }}>
                      <LinearProgressWithLabel
                        value={
                          currentIssuesCountForDashboard?.msg?.connectedCount
                            ? (currentIssuesCountForDashboard.msg
                                .connectedCount /
                                (currentIssuesCountForDashboard?.msg
                                  ?.totalCount || 1)) *
                              100
                            : 0
                        }
                        colors={colors}
                        label={
                          <Box
                            component="span"
                            sx={{
                              cursor: "pointer",
                              "&:hover": { textDecoration: "underline" },
                            }}
                            onClick={() => {
                              const params = new URLSearchParams({
                                status: "Connected",
                              });
                              if (selectedSite)
                                params.append("siteName", selectedSite);
                              if (selectedGroupName)
                                params.append("dgname", selectedGroupName);
                              history.push(`/minerstatus?${params.toString()}`);
                              try {
                                const storageToClear = [
                                  "minerFilters",
                                  "siteGroup",
                                ];
                                storageToClear.forEach((key) =>
                                  sessionStorage.removeItem(key)
                                );
                                const newMinerFilters = { status: "Connected" };
                                const newSiteGroupFilters =
                                  selectedSite !== "" &&
                                  selectedGroupName !== ""
                                    ? {
                                        siteName: selectedSite,
                                        dgname: selectedGroupName,
                                      }
                                    : selectedSite !== ""
                                    ? { siteName: selectedSite }
                                    : {};
                                sessionStorage.setItem(
                                  "minerFilters",
                                  JSON.stringify(newMinerFilters)
                                );
                                sessionStorage.setItem(
                                  "siteGroup",
                                  JSON.stringify(newSiteGroupFilters)
                                );
                              } catch (e) {
                                console.error(
                                  "Failed to update sessionStorage:",
                                  e
                                );
                              }
                            }}
                          >
                            Connected
                          </Box>
                        }
                        count={
                          currentIssuesCountForDashboard?.msg?.connectedCount ||
                          0
                        }
                        total={
                          currentIssuesCountForDashboard?.msg?.totalCount || 0
                        }
                      />
                    </Box>
                    <Box sx={{ paddingBottom: "1rem" }}>
                      <LinearProgressWithLabel
                        value={
                          currentIssuesCountForDashboard?.msg?.onboardedCount
                            ? (currentIssuesCountForDashboard.msg
                                .onboardedCount /
                                (currentIssuesCountForDashboard?.msg
                                  ?.totalCount || 1)) *
                              100
                            : 0
                        }
                        colors={colors}
                        label={
                          <Box
                            component="span"
                            sx={{
                              cursor: "pointer",
                              "&:hover": { textDecoration: "underline" },
                            }}
                            onClick={() => {
                              const params = new URLSearchParams({
                                status: "Onboarded",
                              });
                              if (selectedSite)
                                params.append("siteName", selectedSite);
                              if (selectedGroupName)
                                params.append("dgname", selectedGroupName);
                              history.push(`/minerstatus?${params.toString()}`);
                              try {
                                const storageToClear = [
                                  "minerFilters",
                                  "siteGroup",
                                ];
                                storageToClear.forEach((key) =>
                                  sessionStorage.removeItem(key)
                                );
                                const newMinerFilters = { status: "Onboarded" };
                                const newSiteGroupFilters =
                                  selectedSite !== "" &&
                                  selectedGroupName !== ""
                                    ? {
                                        siteName: selectedSite,
                                        dgname: selectedGroupName,
                                      }
                                    : selectedSite !== ""
                                    ? { siteName: selectedSite }
                                    : {};
                                sessionStorage.setItem(
                                  "minerFilters",
                                  JSON.stringify(newMinerFilters)
                                );
                                sessionStorage.setItem(
                                  "siteGroup",
                                  JSON.stringify(newSiteGroupFilters)
                                );
                              } catch (e) {
                                console.error(
                                  "Failed to update sessionStorage:",
                                  e
                                );
                              }
                            }}
                          >
                            Onboarded
                          </Box>
                        }
                        count={
                          currentIssuesCountForDashboard?.msg?.onboardedCount ||
                          0
                        }
                        total={
                          currentIssuesCountForDashboard?.msg?.totalCount || 0
                        }
                      />
                    </Box>
                    <Box sx={{ paddingBottom: "1rem" }}>
                      <LinearProgressWithLabel
                        value={
                          currentIssuesCountForDashboard?.msg?.standbyCount
                            ? (currentIssuesCountForDashboard.msg.standbyCount /
                                (currentIssuesCountForDashboard?.msg
                                  ?.totalCount || 1)) *
                              100
                            : 0
                        }
                        colors={colors}
                        label={
                          <Box
                            component="span"
                            sx={{
                              cursor: "pointer",
                              "&:hover": { textDecoration: "underline" },
                            }}
                            onClick={() => {
                              const params = new URLSearchParams({
                                status: "Standby Mode",
                              });
                              if (selectedSite)
                                params.append("siteName", selectedSite);
                              if (selectedGroupName)
                                params.append("dgname", selectedGroupName);
                              history.push(`/minerstatus?${params.toString()}`);
                              try {
                                const storageToClear = [
                                  "minerFilters",
                                  "siteGroup",
                                ];
                                storageToClear.forEach((key) =>
                                  sessionStorage.removeItem(key)
                                );
                                const newMinerFilters = {
                                  status: "Standby Mode",
                                };
                                const newSiteGroupFilters =
                                  selectedSite !== "" &&
                                  selectedGroupName !== ""
                                    ? {
                                        siteName: selectedSite,
                                        dgname: selectedGroupName,
                                      }
                                    : selectedSite !== ""
                                    ? { siteName: selectedSite }
                                    : {};
                                sessionStorage.setItem(
                                  "minerFilters",
                                  JSON.stringify(newMinerFilters)
                                );
                                sessionStorage.setItem(
                                  "siteGroup",
                                  JSON.stringify(newSiteGroupFilters)
                                );
                              } catch (e) {
                                console.error(
                                  "Failed to update sessionStorage:",
                                  e
                                );
                              }
                            }}
                          >
                            Standby Mode
                          </Box>
                        }
                        count={
                          currentIssuesCountForDashboard?.msg?.standbyCount || 0
                        }
                        total={
                          currentIssuesCountForDashboard?.msg?.totalCount || 0
                        }
                      />
                    </Box>
                    <LinearProgressWithLabel
                      value={
                        currentIssuesCountForDashboard?.msg?.disconnectedCount
                          ? (currentIssuesCountForDashboard.msg
                              .disconnectedCount /
                              (currentIssuesCountForDashboard?.msg
                                ?.totalCount || 1)) *
                            100
                          : 0
                      }
                      colors={colors}
                      label={
                        <Box
                          component="span"
                          sx={{
                            cursor: "pointer",
                            "&:hover": { textDecoration: "underline" },
                          }}
                          onClick={() => {
                            const params = new URLSearchParams({
                              status: "Disconnected",
                            });
                            if (selectedSite)
                              params.append("siteName", selectedSite);
                            if (selectedGroupName)
                              params.append("dgname", selectedGroupName);
                            history.push(`/minerstatus?${params.toString()}`);
                            try {
                              const storageToClear = [
                                "minerFilters",
                                "siteGroup",
                              ];
                              storageToClear.forEach((key) =>
                                sessionStorage.removeItem(key)
                              );
                              const newMinerFilters = {
                                status: "Disconnected",
                              };
                              const newSiteGroupFilters =
                                selectedSite !== "" && selectedGroupName !== ""
                                  ? {
                                      siteName: selectedSite,
                                      dgname: selectedGroupName,
                                    }
                                  : selectedSite !== ""
                                  ? { siteName: selectedSite }
                                  : {};
                              sessionStorage.setItem(
                                "minerFilters",
                                JSON.stringify(newMinerFilters)
                              );
                              sessionStorage.setItem(
                                "siteGroup",
                                JSON.stringify(newSiteGroupFilters)
                              );
                            } catch (e) {
                              console.error(
                                "Failed to update sessionStorage:",
                                e
                              );
                            }
                          }}
                        >
                          Disconnected
                        </Box>
                      }
                      count={
                        currentIssuesCountForDashboard?.msg
                          ?.disconnectedCount || 0
                      }
                      total={
                        currentIssuesCountForDashboard?.msg?.totalCount || 0
                      }
                    />
                  </Box>
                </Box>
              </MetricCard>
            </Box>
            {!selectedSite && <Box height="1.5rem" />}
          </Box>
        </Grid>
        {selectedSite && (
          <Grid
            container
            className={"home-page-container"}
            display={"flex"}
            flexDirection={"row"}
            sx={{ transition: "all 0.3s ease-in-out" }}
          >
            <Box
              width="100%"
              py={4}
              display="flex"
              flexDirection="column"
              gap={6}
              sx={{
                transition: "width 0.3s ease-in-out",
                borderRadius: "1rem 1rem 1rem 1rem",
              }}
            >
              <Box
                className={"filter-box"}
                display={"flex"}
                flexDirection={"row"}
                justifyContent={"space-between"}
                alignItems={"center"}
                flex={1}
                maxHeight={"2rem"}
              >
                <Box
                  sx={{
                    backgroundColor: colors.background.accent,
                    color: colors.primary.main,
                    padding: "0.5rem 1rem",
                    borderRadius: "1rem",
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                  }}
                >
                  <span
                    className="material-symbols-outlined"
                    style={{ fontSize: "1rem", color: colors.legacy.blue.info }}
                  >
                    analytics
                  </span>
                  <H8TitleMedium style={{ fontSize: "1rem", fontWeight: 400 }}>
                    Analytics
                  </H8TitleMedium>
                </Box>
                <Box
                  display={"flex"}
                  flexDirection={"row"}
                  alignItems={"center"}
                  gap={2}
                >
                  <H8TitleMedium>Time period: </H8TitleMedium>
                  <ClickAwayListener onClickAway={handleClickAway}>
                    <>
                      <OutlinedButton
                        colors={colors}
                        ref={buttonRef}
                        onClick={handleClick}
                        endIcon={
                          Boolean(anchorEl) ? (
                            <KeyboardArrowUpRoundedIcon />
                          ) : (
                            <KeyboardArrowDownRoundedIcon />
                          )
                        }
                        disabled={
                          isDashboardDataFetching ||
                          isDashboardDataLoading ||
                          isDashboardDataRefetching
                        }
                      >
                        {capitalizeString(selectedValue)}
                      </OutlinedButton>
                      <StyledMenu
                        colors={colors}
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl)}
                        onClose={handleClickAway}
                      >
                        {timeRanges.map((calendarView, index) => (
                          <StyledMenuItem
                            colors={colors}
                            value={calendarView.value}
                            key={`menuItem${index}`}
                            selected={calendarView.value === selectedValue}
                            onClick={(event) => {
                              event.stopPropagation();
                              handleMenuClose(calendarView.value);
                            }}
                            sx={{ minWidth: "7rem"}}
                          >
                            {capitalizeString(calendarView.label)}
                          </StyledMenuItem>
                        ))}
                      </StyledMenu>
                    </>
                  </ClickAwayListener>
                </Box>
              </Box>
              <Grid2
                container
                spacing={isExpandedGraphsModeActivated ? 6 : 6}
                justifyContent="center"
                alignItems="center"
                padding={0}
              >
                {(!isEmpty(siteLists) && siteLists?.status === "error") ||
                (!isEmpty(siteLists) &&
                  siteLists?.status === "ok" &&
                  Array.isArray(siteLists?.siteList) &&
                  siteLists?.siteList.length === 0) ? (
                  <Grid2 item xs={12}>
                    <AuradineErrorState
                      message={
                        siteLists.msg
                          ? `  ${siteLists?.msg}. Please contact your superadmin`
                          : `You may not have permissions to any sites. Please contact superadmin`
                      }
                      fontSize="1.125rem"
                    />
                  </Grid2>
                ) : (
                  <>
                    {isDashboardDataFetched &&
                      !(
                        (dashboardData?.status?.toLowerCase() === "ok" &&
                          dashboardData?.msg?.length > 0) ||
                        (dashboardDataLED?.status?.toLowerCase() === "ok" &&
                          Array.isArray(dashboardDataLED?.msg) &&
                          dashboardDataLED?.msg?.length > 0)
                      ) && (
                        <Grid2 item xs={12}>
                          <AuradineDataNotFoundState
                            message={
                              selectedSite !== "" && selectedGroupName === ""
                                ? `No data found for ${selectedSite} !!`
                                : selectedSite !== "" &&
                                  selectedGroupName !== ""
                                ? `No data found for site ${selectedSite} - group ${selectedGroupName} !!`
                                : `No Site or Group selected`
                            }
                          />
                        </Grid2>
                      )}
                    {isDashboardDataFetched &&
                      dashboardData?.status?.toLowerCase() === "error" &&
                      dashboardDataLED?.status?.toLowerCase() === "error" && (
                        <Grid2 item xs={12}>
                          <AuradineErrorState
                            message={`${dashboardData?.msg}`}
                          />
                        </Grid2>
                      )}
                    {!isExpandedGraphsModeActivated ? (
                      <>
                        {/* Issues graph always at full width */}
                        {isDashboardDataFetched &&
                          ((dashboardData?.status?.toLowerCase() === "ok" &&
                            dashboardData?.msg?.length > 0) ||
                            (dashboardDataLED?.status?.toLowerCase() === "ok" &&
                              dashboardDataLED?.msg?.length > 0)) && (
                            <Grid2 item xs={12}>
                              <ChartCard elevation={0} colors={colors}>
                                <LineChart
                                  key="issues"
                                  chartIndex={-1}
                                  heading={issuesGraphData.heading}
                                  subHeading={issuesGraphData.subHeading}
                                  lineChartOptions={issuesGraphData.chartOptions}
                                  hasIssuesData={issuesGraphData.hasIssuesData}
                                  isExpanded={false}
                                  isExpansionRequired={false}
                                />
                              </ChartCard>
                            </Grid2>
                          )}
                        {/* Performance graphs in 2x2 grid with expansion */}
                        {isDashboardDataFetched &&
                          ((dashboardData?.status?.toLowerCase() === "ok" &&
                            dashboardData?.msg?.length > 0) ||
                            (dashboardDataLED?.status?.toLowerCase() === "ok" &&
                              dashboardDataLED?.msg?.length > 0)) &&
                          lineChartsData?.map((chartData, index) => (
                            <Grid2 item xs={6} key={index}>
                              <ChartCard elevation={0} colors={colors}>
                                <LineChart
                                  key={index}
                                  chartIndex={index}
                                  heading={chartData.heading}
                                  subHeading={chartData.subHeading}
                                  lineChartOptions={chartData.chartOptions}
                                  hasIssuesData={chartData.hasIssuesData}
                                  isExpanded={chartData.isExpanded}
                                  isExpansionRequired={chartData.isExpansionRequired}
                                  setExpandedChartIndex={setExpandedChartIndex}
                                  isExpandedGraphsModeActivated={isExpandedGraphsModeActivated}
                                />
                              </ChartCard>
                            </Grid2>
                          ))}
                        {isDashboardDataFetching &&
                          !isDashboardDataFetched && (
                            <>
                              {/* Issues graph skeleton */}
                              <Grid2 item xs={12}>
                                <Skeleton
                                  variant="rectangular"
                                  width={"auto"}
                                  animation={"wave"}
                                  height={300}
                                  sx={{
                                    borderRadius: 2,
                                    backgroundColor: colors.background.secondary,
                                  }}
                                />
                              </Grid2>
                              {/* Performance graph skeletons */}
                              {Array(4)
                                .fill()
                                .map((_, index) => (
                                  <Grid2 item xs={6} key={index}>
                                    <Skeleton
                                      variant="rectangular"
                                      width={"auto"}
                                      animation={"wave"}
                                      height={300}
                                      sx={{
                                        borderRadius: 2,
                                        backgroundColor: colors.background.secondary,
                                      }}
                                    />
                                  </Grid2>
                                ))}
                            </>
                          )}
                      </>
                    ) : (
                      <>
                        {/* Issues graph always visible */}
                        {isDashboardDataFetched &&
                          ((dashboardData?.status?.toLowerCase() === "ok" &&
                            dashboardData?.msg?.length > 0) ||
                            (dashboardDataLED?.status?.toLowerCase() === "ok" &&
                              dashboardDataLED?.msg?.length > 0)) && (
                            <Grid2 item xs={12}>
                              <ChartCard elevation={0} colors={colors}>
                                <LineChart
                                  key="issues"
                                  chartIndex={-1}
                                  heading={issuesGraphData.heading}
                                  subHeading={issuesGraphData.subHeading}
                                  lineChartOptions={issuesGraphData.chartOptions}
                                  hasIssuesData={issuesGraphData.hasIssuesData}
                                  isExpanded={false}
                                  isExpansionRequired={false}
                                />
                              </ChartCard>
                            </Grid2>
                          )}
                        {/* Loading skeleton for expanded mode */}
                        {isDashboardDataFetching &&
                          !isDashboardDataFetched && (
                            <>
                              <Grid2 item xs={12}>
                                <Skeleton
                                  variant="rectangular"
                                  width={"auto"}
                                  animation={"wave"}
                                  height={300}
                                  sx={{
                                    borderRadius: 2,
                                    backgroundColor: colors.background.secondary,
                                  }}
                                />
                              </Grid2>
                              <Grid2 item xs={12}>
                                <Skeleton
                                  variant="rectangular"
                                  width={"auto"}
                                  animation={"wave"}
                                  height={300}
                                  sx={{
                                    borderRadius: 2,
                                    backgroundColor: colors.background.secondary,
                                  }}
                                />
                              </Grid2>
                              {Array(3)
                                .fill()
                                .map((_, index) => (
                                  <Grid2 item xs={2.666} key={index}>
                                    <Skeleton
                                      variant="rectangular"
                                      width={"auto"}
                                      animation={"wave"}
                                      height={200}
                                      sx={{
                                        borderRadius: 2,
                                        backgroundColor: colors.background.secondary,
                                      }}
                                    />
                                  </Grid2>
                                ))}
                            </>
                          )}
                        {/* Expanded chart */}
                        {isDashboardDataFetched &&
                         ((dashboardData?.status?.toLowerCase() === "ok" &&
                         dashboardData?.msg?.length > 0) ||
                         (dashboardDataLED?.status?.toLowerCase() === "ok" &&
                           dashboardDataLED?.msg?.length > 0)) &&
                          expandedChart.map((chartData, index) => (
                            <Grid2 item xs={12} key={`expanded-${index}`}>
                              <ChartCard elevation={0} colors={colors}>
                                <LineChart
                                  key={index}
                                  chartIndex={lineChartsData.findIndex(
                                    (chart) => chart.heading === chartData.heading
                                  )}
                                  heading={chartData.heading}
                                  subHeading={chartData.subHeading}
                                  lineChartOptions={chartData.chartOptions}
                                  hasIssuesData={chartData.hasIssuesData}
                                  isExpanded={true}
                                  isExpansionRequired={true}
                                  setExpandedChartIndex={setExpandedChartIndex}
                                  expandedChartIndex={expandedChartIndex}
                                  isExpandedGraphsModeActivated={isExpandedGraphsModeActivated}
                                />
                              </ChartCard>
                            </Grid2>
                          ))}
                        {/* Other minimized charts */}
                        {isDashboardDataFetched &&
                         ((dashboardData?.status?.toLowerCase() === "ok" &&
                         dashboardData?.msg?.length > 0) ||
                         (dashboardDataLED?.status?.toLowerCase() === "ok" &&
                           dashboardDataLED?.msg?.length > 0)) &&
                          otherCharts.map((chartData, index) => (
                            <Grid2 item xs={2.666} key={`minimized-${index}`}>
                              <MinimizedChartCard elevation={0} colors={colors}>
                                <LineChart
                                  key={index}
                                  chartIndex={lineChartsData.findIndex(
                                    (chart) => chart.heading === chartData.heading
                                  )}
                                  heading={chartData.heading}
                                  subHeading={chartData.subHeading}
                                  lineChartOptions={chartData.chartOptions}
                                  hasIssuesData={chartData.hasIssuesData}
                                  isExpanded={false}
                                  isExpansionRequired={true}
                                  setExpandedChartIndex={setExpandedChartIndex}
                                  expandedChartIndex={expandedChartIndex}
                                  isExpandedGraphsModeActivated={isExpandedGraphsModeActivated}
                                />
                              </MinimizedChartCard>
                            </Grid2>
                          ))}
                      </>
                    )}
                  </>
                )}
              </Grid2>
              <Box height="1.5rem" />
            </Box>
          </Grid>
        )}
      </Box>
    </PageWrapper>
  );
};

export default Home;
